import React from "react";
import Head from "next/head";
import { Box, Heading, VStack, Text, Button, Flex, useColorMode } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const CaseStudiesPage = () => {
  const { colorMode } = useColorMode();
  const bgColor = colorMode === "dark" ? "gray.900" : "gray.50";
  const textColor = colorMode === "dark" ? "white" : "gray.700";
  const router = useRouter();

  const caseStudies = [
    {
      title: "Transforming a Training Institute with Aptofy's AI Solutions",
      description:
        "Discover how Aptofy revolutionized training institutes with AI-powered solutions, ensuring efficiency and scalability.",
      link: "/caseStudy1",
      publishDate: "2024-01-15",
    },
    {
      title: "Building High Performance Teams the Smart and Sustainable Way",
      description:
        "Learn how Aptofy's bias-free solutions enabled sustainable and high-performance hiring on a global scale.",
      link: "/caseStudy2",
      publishDate: "2024-02-01",
    },
    {
      title: "Transforming Global Hiring with Aptofy's AI-Powered Solutions",
      description:
        "Explore how Aptofy's AI-powered platform helped businesses transform their global hiring processes.",
      link: "/caseStudy3",
      publishDate: "2024-02-15",
    },
    {
      title: "Transforming Recruitment for a Scaling Fintech in the BFSI Industry",
      description:
        "Learn how Aptofy's AI-powered recruitment solutions can help fintech companies streamline their hiring process while focusing on quality and diversity.",
      link: "/caseStudy4",
      publishDate: "2024-03-01",
    },
    {
      title: "Empowering Semiconductor & VLSI Startups with Strategic Talent Solutions",
      description: 
        "Discover how Aptofy is revolutionizing talent acquisition for semiconductor startups through AI-powered assessments, university partnerships, and specialized recruitment strategies.",
      link:  "/caseStudy5",
      publishDate: "2024-01-07",
}
  ];

  // Generate structured data for SEO
  const generateStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": caseStudies.map((study, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Article",
        "headline": study.title,
        "description": study.description,
        "datePublished": study.publishDate,
        "url": `https://www.aptofy.com${study.link}`, // Correct domain
      },
    })),
  });

  return (
    <>
      <Head>
        <title>Case Studies | Aptofy - AI-Powered Business Solutions</title>
        <meta
          name="description"
          content="Explore how Aptofy's AI solutions have transformed businesses across industries with real-world case studies."
        />
        <meta
          name="keywords"
          content="AI case studies, business transformation, AI-powered solutions, training institute solutions, recruitment solutions, hiring solutions, Aptofy, case study"
        />
        <meta property="og:title" content="Case Studies | Aptofy - AI-Powered Business Solutions" />
        <meta
          property="og:description"
          content="Explore real-world success stories of how Aptofy's AI solutions transformed businesses."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.aptofy.com/caseStudy" />
        <meta property="og:image" content="https://www.aptofy.com/images/case-studies-banner.jpg" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(generateStructuredData()) }}
        />
      </Head>
      <Box p={8}>
        <Heading size="xl" mb={8} textAlign="center" bg={bgColor}>
          Case Studies
        </Heading>
        <Flex
          direction={["column", "column", "row"]}
          justify="center"
          align="center"
          gap={8}
          flexWrap="wrap"
        >
          {caseStudies.map((study, index) => (
            <MotionBox
              key={index}
              bg={bgColor}
              p={6}
              rounded="lg"
              shadow="md"
              w={["100%", "80%", "45%"]}
              _hover={{ shadow: "lg" }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.2,
                ease: "easeInOut",
              }}
            >
              <VStack align="start" spacing={4}>
                <Heading size="md" color="blue.500">
                  {study.title}
                </Heading>
                <Text color={textColor}>{study.description}</Text>
                <Button
                  colorScheme="blue"
                  onClick={() => router.push(study.link)}
                >
                  Read More
                </Button>
              </VStack>
            </MotionBox>
          ))}
        </Flex>
      </Box>
    </>
  );
};

export default CaseStudiesPage;
